import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  getUserLevels,
  createUserLevel,
  updateUserLevel,
  deleteUserLevel,
  UserLevel,
  CreateUserLevelRequest,
  UpdateUserLevelRequest,
} from '../services/userService';

export default function UserLevelsPage() {
  const { t } = useTranslation();
  const [levels, setLevels] = useState<UserLevel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLevel, setEditingLevel] = useState<UserLevel | null>(null);
  
  const [form, setForm] = useState<CreateUserLevelRequest>({
    name: '',
    description: '',
    level: 1,
    color: '#4CAF50',
  });

  useEffect(() => {
    loadLevels();
  }, []);

  const loadLevels = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getUserLevels();
      setLevels(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load user levels');
      console.error('Error loading levels:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingLevel(null);
    setForm({
      name: '',
      description: '',
      level: levels.length + 1,
      color: '#4CAF50',
    });
    setDialogOpen(true);
  };

  const handleEdit = (level: UserLevel) => {
    setEditingLevel(level);
    setForm({
      name: level.name,
      description: level.description || '',
      level: level.level,
      color: level.color || '#4CAF50',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editingLevel) {
        await updateUserLevel(editingLevel.id, form as UpdateUserLevelRequest);
      } else {
        await createUserLevel(form);
      }
      setDialogOpen(false);
      loadLevels();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save user level');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('userLevels.confirmDelete', 'Are you sure you want to delete this level?'))) {
      return;
    }
    
    try {
      await deleteUserLevel(id);
      loadLevels();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete user level');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          {t('userLevels.title', 'User Levels')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAdd}
        >
          {t('userLevels.addLevel', 'Add Level')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('userLevels.order', 'Order')}</TableCell>
              <TableCell>{t('userLevels.name', 'Name')}</TableCell>
              <TableCell>{t('userLevels.description', 'Description')}</TableCell>
              <TableCell>{t('userLevels.color', 'Color')}</TableCell>
              <TableCell>{t('userLevels.userCount', 'Users')}</TableCell>
              <TableCell align="right">{t('common.actions', 'Actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {levels.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="textSecondary">
                    {t('userLevels.noLevels', 'No user levels defined')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              levels.map((level) => (
                <TableRow key={level.id} hover>
                  <TableCell>
                    <Chip label={level.level} size="small" />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={level.name}
                      sx={{
                        backgroundColor: level.color || '#757575',
                        color: 'white',
                      }}
                    />
                  </TableCell>
                  <TableCell>{level.description || '-'}</TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '4px',
                        backgroundColor: level.color || '#757575',
                        border: '1px solid #ccc',
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip label={level.userCount} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleEdit(level)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(level.id)}
                      disabled={level.userCount > 0}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingLevel
            ? t('userLevels.editLevel', 'Edit Level')
            : t('userLevels.addLevel', 'Add Level')}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label={t('userLevels.name', 'Name')}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              fullWidth
              required
              placeholder="Junior Developer, Senior Engineer, etc."
            />
            <TextField
              label={t('userLevels.description', 'Description')}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
              placeholder="Brief description of this level..."
            />
            <TextField
              type="number"
              label={t('userLevels.order', 'Order')}
              value={form.level}
              onChange={(e) => setForm({ ...form, level: parseInt(e.target.value) || 1 })}
              fullWidth
              required
              inputProps={{ min: 1, max: 100 }}
              helperText={t('userLevels.orderHelp', 'Lower numbers appear first')}
            />
            <Box>
              <Typography variant="body2" gutterBottom>
                {t('userLevels.color', 'Color')}
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <input
                  type="color"
                  value={form.color}
                  onChange={(e) => setForm({ ...form, color: e.target.value })}
                  style={{ width: 50, height: 40, cursor: 'pointer' }}
                />
                <TextField
                  value={form.color}
                  onChange={(e) => setForm({ ...form, color: e.target.value })}
                  size="small"
                  placeholder="#4CAF50"
                />
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
          <Button onClick={handleSave} variant="contained" disabled={!form.name || form.level < 1}>
            {t('common.save', 'Save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
